import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Check, X, AlertCircle, CheckCircle } from 'lucide-react';
import { CustomerService } from '../services/CustomerService';
import { PointHistory, PointHistoryListResponse, CUSTOMER_LIMIT_OPTIONS } from '../models/CustomerModels';
import PointHistoryTable from './PointHistoryTable';
import Pagination from './Pagination';
import { colors, typography, spacing, borderRadius, shadows, animations, zIndex } from '../styles/designTokens';
import Button from './ui/Button';
import Input from './ui/Input';

// 컨테이너 스타일
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

// 에러 알림 스타일
const ErrorAlert = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 20px;
  background: ${colors.warning[100]};
  border: 1px solid ${colors.warning[300]};
  border-radius: ${borderRadius.lg};
  color: ${colors.warning[800]};
  font-size: 14px;

  .icon {
    margin-right: 12px;
    font-size: 16px;
  }

  .close-btn {
    margin-left: auto;
    background: none;
    border: none;
    color: ${colors.warning[800]};
    cursor: pointer;
    font-size: 18px;
    padding: 0;
    
    &:hover {
      opacity: 0.7;
    }
  }
`;

// 헤더 스타일
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;



// 인라인 포인트 관리 폼 스타일 - 컴팩트
const InlinePointForm = styled.div`
  background: ${colors.semantic.background.secondary};
  border: 1px solid ${colors.semantic.border.primary};
  border-radius: ${borderRadius.lg};
  padding: ${spacing[4]};
  margin-bottom: ${spacing[4]};
  box-shadow: ${shadows.sm};
  transition: all ${animations.duration.normal} ${animations.easing.smooth};
  width: 100%;
  
  &:hover {
    box-shadow: ${shadows.base};
  }
`;

// 폼 컨테이너 스타일 - 한 줄 레이아웃 (균등 분할)
const FormContainer = styled.div`
  display: grid;
  grid-template-columns: auto 1fr 1fr 1fr auto;
  gap: ${spacing[3]};
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${spacing[2]};
  }
`;

// 포인트 타입 선택 스타일 - 균등 분할 최적화
const PointTypeSelect = styled.select`
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid ${colors.semantic.border.primary};
  border-radius: ${borderRadius.md};
  background: ${colors.semantic.background.primary};
  color: ${colors.semantic.text.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  font-family: ${typography.fontFamily.sans};
  cursor: pointer;
  transition: all ${animations.duration.normal} ${animations.easing.smooth};
  width: 100%;
  height: 40px;
  min-width: 120px;
  
  &:focus {
    outline: none;
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 2px ${colors.primary[100]};
  }
  
  &:hover {
    border-color: ${colors.semantic.border.secondary};
  }
`;

// 포인트 금액 입력 래퍼 스타일
const PointAmountWrapper = styled.div`
  position: relative;
  width: 100%;
  
  .point-addon {
    position: absolute;
    top: 50%;
    right: ${spacing[3]};
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${colors.primary[600]};
    font-size: ${typography.fontSize.sm};
    font-weight: ${typography.fontWeight.bold};
    background: linear-gradient(135deg, ${colors.primary[50]} 0%, ${colors.primary[100]} 100%);
    border: 1px solid ${colors.primary[200]};
    border-radius: ${borderRadius.md};
    padding: ${spacing[1]} ${spacing[2]};
    z-index: 1;
    box-shadow: 0 1px 2px rgba(59, 130, 246, 0.1);
    transition: all ${animations.duration.normal} ${animations.easing.smooth};
    
    &:hover {
      background: linear-gradient(135deg, ${colors.primary[100]} 0%, ${colors.primary[200]} 100%);
      border-color: ${colors.primary[300]};
      box-shadow: 0 2px 4px rgba(59, 130, 246, 0.15);
    }
  }
`;

// 액션 버튼 컨테이너 스타일
const FormActions = styled.div`
  display: flex;
  gap: ${spacing[2]};
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

// 메시지 스타일
const MessageContainer = styled.div<{ type: 'success' | 'error' }>`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[3]} ${spacing[4]};
  border-radius: ${borderRadius.lg};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  margin-top: ${spacing[3]};
  animation: ${animations.keyframes.slideUp} ${animations.duration.normal} ${animations.easing.smooth};
  
  ${props => props.type === 'success' ? `
    background: ${colors.success[50]};
    color: ${colors.success[700]};
    border: 1px solid ${colors.success[200]};
  ` : `
    background: ${colors.error[50]};
    color: ${colors.error[700]};
    border: 1px solid ${colors.error[200]};
  `}
`;

// 포인트 미리보기 스타일 - 균등 분할
const PointPreview = styled.div`
  background: ${colors.semantic.background.primary};
  border: 1px solid ${colors.semantic.border.primary};
  border-radius: ${borderRadius.md};
  padding: ${spacing[2]} ${spacing[3]};
  font-size: ${typography.fontSize.xs};
  color: ${colors.semantic.text.primary};
  font-weight: ${typography.fontWeight.medium};
  min-height: 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  transition: all ${animations.duration.normal} ${animations.easing.smooth};
  width: 100%;
  
  .preview-label {
    color: ${colors.semantic.text.tertiary};
    font-size: 10px;
    margin-bottom: 2px;
    font-weight: ${typography.fontWeight.medium};
  }
  
  .preview-content {
    color: ${colors.semantic.text.primary};
    font-weight: ${typography.fontWeight.semibold};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

// 헤더 정보 스타일
const HeaderInfo = styled.div`
  h5 {
    font-size: 20px;
    font-weight: 700;
    color: ${colors.secondary[800]};
    margin: 0 0 4px 0;
  }

  .subtitle {
    font-size: 14px;
    color: ${colors.semantic.text.tertiary};
    margin: 0;
  }
`;

// 페이지 크기 선택 컨트롤 스타일
const LimitControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  label {
    font-size: 14px;
    color: ${colors.semantic.text.secondary};
    font-weight: 500;
    margin: 0;
  }

  select {
    padding: 8px 12px;
    border: 1px solid ${colors.neutral[300]};
    border-radius: ${borderRadius.md};
    background: ${colors.semantic.background.primary};
    color: ${colors.semantic.text.secondary};
    font-size: 14px;
    cursor: pointer;
    transition: border-color 0.2s;

    &:focus {
      outline: none;
      border-color: ${colors.primary[500]};
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    &:disabled {
      background: ${colors.neutral[50]};
      color: ${colors.semantic.text.disabled};
      cursor: not-allowed;
    }
  }
`;

// 로딩 오버레이 스타일
const LoadingOverlay = styled.div`
  position: relative;
  
  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: ${zIndex.docked};
    min-height: 200px;
    border-radius: 12px;
  }
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid ${colors.neutral[100]};
    border-top: 3px solid ${colors.primary[500]};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// 모바일에서만 테이블 좌우 스크롤 래퍼
const MobileTableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    table {
      min-width: 600px;
      width: 100%;
    }
    th, td {
      white-space: nowrap;
    }
  }
`;

// 포인트 내역 테이블 컨테이너 Props 인터페이스
interface PointHistoryTableContainerProps {
  userIdx: number;
  onPointUpdate?: () => void; // 포인트 업데이트 콜백
}

// 포인트 내역 테이블 컨테이너 컴포넌트
const PointHistoryTableContainer: React.FC<PointHistoryTableContainerProps> = ({ userIdx, onPointUpdate }) => {
  // 상태 관리
  const [pointHistory, setPointHistory] = useState<PointHistory[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(15);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);

  // 인라인 포인트 관리 폼 상태
  const [pointType, setPointType] = useState<boolean>(true); // true: 충전, false: 차감
  const [pointAmount, setPointAmount] = useState<string>('');
  const [pointDesc, setPointDesc] = useState<string>('');
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const [formMessage, setFormMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 포인트 내역 조회 함수
  const fetchPointHistory = useCallback(async (page: number = 1, pageLimit: number = 15) => {
    try {
      setLoading(true);
      setError(null);

      console.log('포인트 내역 조회 시작:', { userIdx, page, pageLimit });

      const response: PointHistoryListResponse = await CustomerService.getPointHistory({
        userIdx,
        page,
        limit: pageLimit
      });

      console.log('포인트 내역 조회 성공:', response);

      setPointHistory(response.items);
      setTotal(response.total);
      setTotalPages(response.total_pages);
      setCurrentPage(response.page);
      setLimit(response.limit);

    } catch (error: any) {
      console.error('포인트 내역 조회 실패:', error);
      setError('포인트 내역을 불러오는 중 오류가 발생했습니다.');
      return;
    } finally {
      setLoading(false);
    }
  }, [userIdx]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchPointHistory(1, limit);
  }, [fetchPointHistory, limit]);

  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    if (page !== currentPage) {
      fetchPointHistory(page, limit);
    }
  };

  // 페이지 크기 변경 핸들러
  const handleLimitChange = (newLimit: number) => {
    if (newLimit !== limit) {
      setLimit(newLimit);
      fetchPointHistory(1, newLimit);
    }
  };

  // 포인트 타입 변경 핸들러
  const handlePointTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPointType(e.target.value === 'true');
    setFormMessage(null);
  };

  // 포인트 금액 변경 핸들러 - 회계처리 반영
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // 빈 값이거나 숫자만 입력 허용 (콤마 제거 후 검증)
    const numericValue = value.replace(/,/g, '');
    if (value === '' || /^\d+$/.test(numericValue)) {
      // 숫자에 천 단위 콤마 추가
      if (numericValue === '') {
        setPointAmount('');
      } else {
        const formattedValue = parseInt(numericValue).toLocaleString();
        setPointAmount(formattedValue);
      }
      setFormMessage(null);
    }
  };

  // 포인트 설명 변경 핸들러
  const handleDescChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPointDesc(e.target.value);
    setFormMessage(null);
  };

  // 폼 초기화
  const resetForm = () => {
    setPointAmount('');
    setPointDesc('');
    setFormMessage(null);
  };

  // 유효성 검사
  const validateForm = () => {
    if (!pointAmount || pointAmount === '0') {
      setFormMessage({ type: 'error', text: '포인트 금액을 입력해주세요.' });
      return false;
    }

    const amount = parseInt(pointAmount);
    if (amount <= 0) {
      setFormMessage({ type: 'error', text: '포인트 금액은 0보다 커야 합니다.' });
      return false;
    }

    if (amount > 1000000) {
      setFormMessage({ type: 'error', text: '포인트 금액은 1,000,000을 초과할 수 없습니다.' });
      return false;
    }

    if (!pointDesc.trim()) {
      setFormMessage({ type: 'error', text: '포인트 설명을 입력해주세요.' });
      return false;
    }

    if (pointDesc.trim().length > 100) {
      setFormMessage({ type: 'error', text: '포인트 설명은 100자를 초과할 수 없습니다.' });
      return false;
    }

    return true;
  };

  // 포인트 처리 핸들러
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setFormLoading(true);
    setFormMessage(null);

    try {
      const result = await CustomerService.updatePoints({
        userIdx,
        pointType,
        pointAmount: parseInt(pointAmount.replace(/,/g, '')),
        pointDesc: pointDesc.trim()
      });

      if (result.success) {
        setFormMessage({
          type: 'success',
          text: result.message
        });
        resetForm();
        // 포인트 내역 새로고침
        fetchPointHistory(currentPage, limit);
        // 상위 컴포넌트에 포인트 업데이트 알림
        if (onPointUpdate) {
          onPointUpdate();
        }
        // 3초 후 성공 메시지 자동 제거
        setTimeout(() => {
          setFormMessage(null);
        }, 3000);
      } else {
        setFormMessage({
          type: 'error',
          text: result.message
        });
      }
    } catch (err) {
      console.error('포인트 처리 실패:', err);
      setFormMessage({
        type: 'error',
        text: '포인트 처리 중 오류가 발생했습니다.'
      });
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <Container>
      {/* 에러 메시지 */}
      {error && (
        <ErrorAlert>
          <span className="icon">⚠️</span>
          {error}
          <button
            className="close-btn"
            onClick={() => setError(null)}
            aria-label="닫기"
          >
            ×
          </button>
        </ErrorAlert>
      )}

      {/* 인라인 포인트 관리 폼 - 한 줄 레이아웃 */}
      <InlinePointForm>
        <FormContainer>
          <PointTypeSelect
            value={pointType.toString()}
            onChange={handlePointTypeChange}
            disabled={formLoading}
          >
            <option value="true">💎 충전</option>
            <option value="false">📉 차감</option>
          </PointTypeSelect>

          <Input
            type="text"
            value={pointDesc}
            onChange={handleDescChange}
            placeholder="포인트 설명"
            disabled={formLoading}
            size="md"
            variant="default"
          />

          <PointAmountWrapper>
            <Input
              type="text"
              value={pointAmount}
              onChange={handleAmountChange}
              placeholder="금액"
              disabled={formLoading}
              size="md"
              variant="default"
            />
            <div className="point-addon">P</div>
          </PointAmountWrapper>

          {/* 포인트 미리보기 - 항상 표시 */}
          <PointPreview>
            <div className="preview-label">미리보기</div>
            <div className="preview-content">
              {pointAmount && pointDesc ?
                `[${pointDesc}] ${pointAmount} 포인트 ${pointType ? '충전' : '차감'}` :
                '입력하세요'
              }
            </div>
          </PointPreview>

          <FormActions>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={formLoading || !pointAmount || !pointDesc.trim()}
              state={formLoading ? 'loading' : 'default'}
              leftIcon={<Check size={14} />}
            >
              {formLoading ? '처리 중...' : '처리'}
            </Button>

            <Button
              variant="secondary"
              size="sm"
              onClick={resetForm}
              disabled={formLoading}
              leftIcon={<X size={14} />}
            >
              초기화
            </Button>
          </FormActions>
        </FormContainer>

        {/* 메시지 */}
        {formMessage && (
          <MessageContainer type={formMessage.type}>
            {formMessage.type === 'success' ? (
              <CheckCircle size={16} />
            ) : (
              <AlertCircle size={16} />
            )}
            {formMessage.text}
          </MessageContainer>
        )}
      </InlinePointForm>

      {/* 포인트 내역 헤더 */}
      <Header>
        <HeaderInfo>
          <h5>포인트 내역</h5>
          <p className="subtitle">
            총 {total.toLocaleString()}개의 포인트 내역
          </p>
        </HeaderInfo>

        <LimitControl>
          <label htmlFor="limitSelect">페이지당 표시:</label>
          <select
            id="limitSelect"
            value={limit}
            onChange={(e) => handleLimitChange(Number(e.target.value))}
            disabled={loading || formLoading}
          >
            {CUSTOMER_LIMIT_OPTIONS.map(option => (
              <option key={option} value={option}>
                {option}개
              </option>
            ))}
          </select>
        </LimitControl>
      </Header>

      {/* 포인트 내역 테이블 */}
      <LoadingOverlay>
        {/* 모바일에서만 좌우 스크롤 가능하도록 래퍼로 감쌈 */}
        <MobileTableScrollWrapper>
          <PointHistoryTable
            data={pointHistory}
            loading={loading}
          />
        </MobileTableScrollWrapper>
        {/* 로딩 오버레이 */}
        {loading && (
          <div className="overlay">
            <div className="spinner" />
          </div>
        )}
      </LoadingOverlay>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onPageChange={handlePageChange}
        />
      )}

    </Container>
  );
};

export default PointHistoryTableContainer; 